/**
 * Corporate Action Form Component
 *
 * Allows users to create corporate actions (share splits, consolidations, bonus shares).
 * Shows impact preview and warning about permanent changes.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  LayoutChangeEvent,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { DatePicker } from '../ui/DatePicker';
import { SymbolAutocomplete } from './SymbolAutocomplete';
import { Ionicons } from '@expo/vector-icons';

export type CorporateActionType = 'split' | 'consolidate' | 'bonus';

export interface CorporateActionData {
  position_id: number;
  action_type: CorporateActionType;
  from_shares: number;
  to_shares: number;
  exclude_date: string; // YYYY-MM-DD format
  description: string;
}

interface CorporateActionFormProps {
  onSubmit: (data: CorporateActionData) => void;
  onCancel?: () => void;
  loading?: boolean;
  positionId?: number; // Pre-filled position ID (required for new API)
  symbol?: string; // For display purposes only
  readOnlySymbol?: boolean; // When true, symbol cannot be edited
  instrumentName?: string; // Pre-filled instrument name (optional, to avoid extra API call)
}

export function CorporateActionForm({
  onSubmit,
  onCancel,
  loading = false,
  positionId,
  symbol: prefillSymbol = '',
  readOnlySymbol = false,
  instrumentName: prefillInstrumentName,
}: CorporateActionFormProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [symbol, setSymbol] = useState(prefillSymbol);
  const [actionType, setActionType] = useState<CorporateActionType>('split');

  // Intuitive ratio inputs
  const [ratioFrom, setRatioFrom] = useState('1'); // For split/consolidate: "from" shares
  const [ratioTo, setRatioTo] = useState('2'); // For split/consolidate: "to" shares
  const [bonusShares, setBonusShares] = useState('1'); // For bonus: free shares received
  const [everyShares, setEveryShares] = useState('5'); // For bonus: per X shares owned

  const [excludeDate, setExcludeDate] = useState(() => {
    // Default to today's date in YYYY-MM-DD format
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [description, setDescription] = useState('');
  const [showDateHelp, setShowDateHelp] = useState(false);

  const [errors, setErrors] = useState<{
    symbol?: string;
    ratio?: string;
    exclude_date?: string;
  }>({});

  // Track help icon position for callout placement
  const helpIconRef = useRef<View>(null);
  const outerContainerRef = useRef<View>(null);
  const [helpIconLayout, setHelpIconLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const measureHelpIcon = useCallback(() => {
    if (helpIconRef.current && outerContainerRef.current) {
      helpIconRef.current.measureLayout(
        outerContainerRef.current as any,
        (x, y, width, height) => {
          setHelpIconLayout({ x, y, width, height });
        },
        () => {}
      );
    }
  }, []);

  // Get formatted ratio string for display
  const getRatioDisplay = (): string => {
    switch (actionType) {
      case 'split':
        return `${ratioFrom}-for-${ratioTo}`;
      case 'consolidate':
        return `${ratioTo}-for-${ratioFrom}`;
      case 'bonus':
        return `${bonusShares}-for-${everyShares}`;
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!positionId) {
      newErrors.symbol = t('validation.positionRequired', { defaultValue: 'Position is required' });
    } else if (!symbol.trim()) {
      newErrors.symbol = t('validation.symbolRequired', { defaultValue: 'Symbol is required' });
    }

    // Validate ratio inputs
    const from = parseFloat(ratioFrom);
    const to = parseFloat(ratioTo);
    const bonus = parseFloat(bonusShares);
    const every = parseFloat(everyShares);

    if (actionType === 'bonus') {
      if (!bonusShares || isNaN(bonus) || bonus <= 0) {
        newErrors.ratio = t('validation.invalidBonusShares', {
          defaultValue: 'Invalid bonus shares (must be positive number)',
        });
      } else if (!everyShares || isNaN(every) || every <= 0) {
        newErrors.ratio = t('validation.invalidEveryShares', {
          defaultValue: 'Invalid "for every" shares (must be positive number)',
        });
      }
    } else {
      if (!ratioFrom || isNaN(from) || from <= 0) {
        newErrors.ratio = t('validation.invalidFromShares', {
          defaultValue: 'Invalid "from" shares (must be positive number)',
        });
      } else if (!ratioTo || isNaN(to) || to <= 0) {
        newErrors.ratio = t('validation.invalidToShares', {
          defaultValue: 'Invalid "to" shares (must be positive number)',
        });
      }
    }

    if (!excludeDate.trim()) {
      newErrors.exclude_date = t('validation.excludeDateRequired', {
        defaultValue: 'Exclude date is required',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (loading) return;

    if (validateForm()) {
      const from = parseFloat(ratioFrom) || 1;
      const to = parseFloat(ratioTo) || 1;
      const bonus = parseFloat(bonusShares) || 1;
      const every = parseFloat(everyShares) || 1;

      let from_shares: number;
      let to_shares: number;

      switch (actionType) {
        case 'split':
          // 2-for-1 split: from_shares=1, to_shares=2
          from_shares = from;
          to_shares = to;
          break;
        case 'consolidate':
          // 1-for-10 consolidation: from_shares=10, to_shares=1
          from_shares = from;
          to_shares = to;
          break;
        case 'bonus':
          // 1 bonus for every 5 shares: from_shares=5, to_shares=1
          from_shares = every;
          to_shares = bonus;
          break;
        default:
          from_shares = 1;
          to_shares = 1;
      }

      if (!positionId) {
        setErrors({
          ...errors,
          symbol: t('validation.positionRequired', { defaultValue: 'Position ID is required' }),
        });
        return;
      }

      const formData: CorporateActionData = {
        position_id: positionId,
        action_type: actionType,
        from_shares,
        to_shares,
        exclude_date: excludeDate,
        description: description.trim(),
      };

      onSubmit(formData);
    }
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    if (!symbol.trim() || !excludeDate.trim() || loading) {
      return false;
    }
    const from = parseFloat(ratioFrom);
    const to = parseFloat(ratioTo);
    const bonus = parseFloat(bonusShares);
    const every = parseFloat(everyShares);

    if (actionType === 'bonus') {
      return bonusShares && !isNaN(bonus) && bonus > 0 && everyShares && !isNaN(every) && every > 0;
    }
    return ratioFrom && !isNaN(from) && from > 0 && ratioTo && !isNaN(to) && to > 0;
  };

  // Get action type info
  const actionTypes = [
    {
      value: 'split' as CorporateActionType,
      label: t('portfolio.shareSplit', { defaultValue: 'Share Split' }),
      description: t('portfolio.splitDescription', {
        defaultValue: 'Each share becomes multiple shares',
      }),
      example: t('portfolio.splitExample', { defaultValue: '1 share becomes 2 shares' }),
    },
    {
      value: 'consolidate' as CorporateActionType,
      label: t('portfolio.consolidation', { defaultValue: 'Share Consolidation' }),
      description: t('portfolio.consolidateDescription', {
        defaultValue: 'Multiple shares combine into one',
      }),
      example: t('portfolio.consolidateExample', { defaultValue: '10 shares become 1 share' }),
    },
    {
      value: 'bonus' as CorporateActionType,
      label: t('portfolio.bonusShares', { defaultValue: 'Bonus Shares' }),
      description: t('portfolio.bonusDescription', {
        defaultValue: 'Get free shares based on your holdings',
      }),
      example: t('portfolio.bonusExample', { defaultValue: '1 free share for every 5 shares' }),
    },
  ];

  // Get ratio input labels based on action type
  const getRatioLabels = () => {
    switch (actionType) {
      case 'split':
        return {
          fromLabel: t('portfolio.fromShares', { defaultValue: 'From' }),
          toLabel: t('portfolio.toShares', { defaultValue: 'To' }),
          middleText: t('portfolio.sharesBecome', { defaultValue: 'shares become' }),
        };
      case 'consolidate':
        return {
          fromLabel: t('portfolio.fromShares', { defaultValue: 'From' }),
          toLabel: t('portfolio.toShares', { defaultValue: 'To' }),
          middleText: t('portfolio.sharesBecome', { defaultValue: 'shares become' }),
        };
      case 'bonus':
        return {
          bonusLabel: t('portfolio.freeShares', { defaultValue: 'Free Shares' }),
          middleText: t('portfolio.forEvery', { defaultValue: 'for every' }),
          everyLabel: t('portfolio.sharesOwned', { defaultValue: 'shares owned' }),
        };
      default:
        return {};
    }
  };

  const ratioLabels = getRatioLabels();

  return (
    <View ref={outerContainerRef} style={styles.outerContainer}>
      <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={[styles.formContainer, { backgroundColor: theme.colors.surface }]}>
          {/* Symbol Input */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.symbolRequired', { defaultValue: 'Symbol *' })}
            </Text>
            <SymbolAutocomplete
              value={symbol}
              onChangeText={(text) => {
                setSymbol(text);
                setErrors({ ...errors, symbol: undefined });
              }}
              placeholder={t('portfolio.symbolPlaceholder', { defaultValue: 'e.g., AAPL' })}
              error={errors.symbol}
              editable={!loading && !readOnlySymbol}
              readOnly={readOnlySymbol}
              instrumentName={prefillInstrumentName}
              positionsOnly={true}
            />
          </View>

          {/* Corporate Action Type */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.corporateActionTypeRequired', {
                defaultValue: 'Corporate Action Type *',
              })}
            </Text>
            <View style={styles.actionTypesContainer}>
              <TouchableOpacity
                style={[
                  styles.actionTypeButton,
                  {
                    backgroundColor:
                      actionType === 'split'
                        ? theme.colors.primary
                        : theme.colors.backgroundElevated,
                    borderColor:
                      actionType === 'split' ? theme.colors.primary : theme.colors.border,
                    borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                  },
                ]}
                onPress={() => setActionType('split')}
                disabled={loading}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.actionTypeTitle,
                    {
                      color: actionType === 'split' ? theme.colors.textInverse : theme.colors.text,
                    },
                  ]}
                >
                  {t('portfolio.shareSplit', { defaultValue: 'Share Split' })}
                </Text>
                <Text
                  style={[
                    styles.actionTypeDescription,
                    {
                      color:
                        actionType === 'split'
                          ? theme.colors.textInverse + '99'
                          : theme.colors.textSecondary,
                    },
                  ]}
                  numberOfLines={2}
                >
                  {t('portfolio.splitDescription', {
                    defaultValue: 'Each share becomes multiple shares',
                  })}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.actionTypeButton,
                  {
                    backgroundColor:
                      actionType === 'consolidate'
                        ? theme.colors.primary
                        : theme.colors.backgroundElevated,
                    borderColor:
                      actionType === 'consolidate' ? theme.colors.primary : theme.colors.border,
                    borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                  },
                ]}
                onPress={() => setActionType('consolidate')}
                disabled={loading}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.actionTypeTitle,
                    {
                      color:
                        actionType === 'consolidate' ? theme.colors.textInverse : theme.colors.text,
                    },
                  ]}
                >
                  {t('portfolio.consolidation', { defaultValue: 'Share Consolidation' })}
                </Text>
                <Text
                  style={[
                    styles.actionTypeDescription,
                    {
                      color:
                        actionType === 'consolidate'
                          ? theme.colors.textInverse + '99'
                          : theme.colors.textSecondary,
                    },
                  ]}
                  numberOfLines={2}
                >
                  {t('portfolio.consolidateDescription', {
                    defaultValue: 'Multiple shares combine into one',
                  })}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.actionTypeButton,
                  {
                    backgroundColor:
                      actionType === 'bonus'
                        ? theme.colors.primary
                        : theme.colors.backgroundElevated,
                    borderColor:
                      actionType === 'bonus' ? theme.colors.primary : theme.colors.border,
                    borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                  },
                ]}
                onPress={() => setActionType('bonus')}
                disabled={loading}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.actionTypeTitle,
                    {
                      color: actionType === 'bonus' ? theme.colors.textInverse : theme.colors.text,
                    },
                  ]}
                >
                  {t('portfolio.bonusShares', { defaultValue: 'Bonus Shares' })}
                </Text>
                <Text
                  style={[
                    styles.actionTypeDescription,
                    {
                      color:
                        actionType === 'bonus'
                          ? theme.colors.textInverse + '99'
                          : theme.colors.textSecondary,
                    },
                  ]}
                  numberOfLines={2}
                >
                  {t('portfolio.bonusDescription', {
                    defaultValue: 'Get free shares based on your holdings',
                  })}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Ratio Inputs - Intuitive format */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.ratioDetails', { defaultValue: 'Ratio Details *' })}
            </Text>

            {actionType === 'bonus' ? (
              // Bonus Shares: [X] shares for every [Y] shares
              <View style={styles.ratioInputContainer}>
                <TextInput
                  style={[
                    styles.ratioInput,
                    styles.flexInput,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                    errors.ratio && styles.inputError,
                  ]}
                  value={bonusShares}
                  onChangeText={(text) => {
                    setBonusShares(text);
                    setErrors({ ...errors, ratio: undefined });
                  }}
                  placeholder="1"
                  placeholderTextColor={theme.colors.textTertiary}
                  keyboardType="decimal-pad"
                  editable={!loading}
                />
                <Text style={[styles.ratioLabel, { color: theme.colors.text }]}>
                  {ratioLabels.middleText}
                </Text>
                <TextInput
                  style={[
                    styles.ratioInput,
                    styles.flexInput,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                    errors.ratio && styles.inputError,
                  ]}
                  value={everyShares}
                  onChangeText={(text) => {
                    setEveryShares(text);
                    setErrors({ ...errors, ratio: undefined });
                  }}
                  placeholder="5"
                  placeholderTextColor={theme.colors.textTertiary}
                  keyboardType="decimal-pad"
                  editable={!loading}
                />
                <Text style={[styles.ratioLabel, { color: theme.colors.text }]}>
                  {ratioLabels.everyLabel}
                </Text>
              </View>
            ) : (
              // Split/Consolidate: [X] shares become [Y] shares
              <View style={styles.ratioInputContainer}>
                <TextInput
                  style={[
                    styles.ratioInput,
                    styles.flexInput,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                    errors.ratio && styles.inputError,
                  ]}
                  value={ratioFrom}
                  onChangeText={(text) => {
                    setRatioFrom(text);
                    setErrors({ ...errors, ratio: undefined });
                  }}
                  placeholder={actionType === 'split' ? '1' : '10'}
                  placeholderTextColor={theme.colors.textTertiary}
                  keyboardType="decimal-pad"
                  editable={!loading}
                />
                <Text style={[styles.ratioLabel, { color: theme.colors.text }]}>
                  {ratioLabels.middleText}
                </Text>
                <TextInput
                  style={[
                    styles.ratioInput,
                    styles.flexInput,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                    errors.ratio && styles.inputError,
                  ]}
                  value={ratioTo}
                  onChangeText={(text) => {
                    setRatioTo(text);
                    setErrors({ ...errors, ratio: undefined });
                  }}
                  placeholder={actionType === 'split' ? '2' : '1'}
                  placeholderTextColor={theme.colors.textTertiary}
                  keyboardType="decimal-pad"
                  editable={!loading}
                />
                <Text style={[styles.ratioLabel, { color: theme.colors.text }]}>
                  {t('portfolio.shares', { defaultValue: 'shares' })}
                </Text>
              </View>
            )}

            {errors.ratio && (
              <Text style={[styles.errorText, { color: theme.colors.error }]}>{errors.ratio}</Text>
            )}

            {/* Example hint */}
            <View
              style={[
                styles.hintBox,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
            >
              <Text style={[styles.hintText, { color: theme.colors.textSecondary }]}>
                {t('portfolio.example', { defaultValue: 'Example' })}:{' '}
                {actionTypes.find((a) => a.value === actionType)?.example}
              </Text>
            </View>
          </View>

          {/* Exclude Date */}
          <View style={styles.fieldContainer}>
            <View style={styles.labelWithHelp}>
              <Text style={[styles.label, styles.labelInline, { color: theme.colors.text }]}>
                {t('portfolio.excludeDate', { defaultValue: 'Exclude Date *' })}
              </Text>
              <View ref={helpIconRef} style={styles.helpIconWrapper} onLayout={measureHelpIcon}>
                <TouchableOpacity
                  style={styles.helpIconTouchable}
                  onPress={() => {
                    measureHelpIcon();
                    setShowDateHelp(!showDateHelp);
                  }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color={theme.colors.textSecondary}
                  />
                </TouchableOpacity>
              </View>
            </View>
            <DatePicker
              value={excludeDate}
              onChange={(date) => {
                setExcludeDate(date || '');
                setErrors({ ...errors, exclude_date: undefined });
              }}
              placeholder={t('portfolio.selectDate', { defaultValue: 'Select Date' })}
            />
            {errors.exclude_date && (
              <Text style={[styles.errorText, { color: theme.colors.error }]}>
                {errors.exclude_date}
              </Text>
            )}
          </View>

          {/* Description */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.description', { defaultValue: 'Description (Optional)' })}
            </Text>
            <TextInput
              style={[
                styles.textArea,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              value={description}
              onChangeText={setDescription}
              placeholder={t('portfolio.caDescriptionPlaceholder', {
                defaultValue: 'e.g., 2-for-1 stock split announced by AAPL',
              })}
              placeholderTextColor={theme.colors.textTertiary}
              multiline
              numberOfLines={3}
              maxLength={100}
              editable={!loading}
              textAlignVertical="top"
            />
            <Text style={[styles.characterCount, { color: theme.colors.textTertiary }]}>
              {description.length}/100
            </Text>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[
                styles.button,
                styles.primaryButton,
                { backgroundColor: isFormValid() ? theme.colors.primary : theme.colors.border },
              ]}
              onPress={handleSubmit}
              disabled={!isFormValid()}
              activeOpacity={0.8}
            >
              {loading ? (
                <ActivityIndicator size="small" color={theme.colors.textInverse} />
              ) : (
                <Text
                  style={[
                    styles.buttonText,
                    { color: isFormValid() ? theme.colors.textInverse : theme.colors.textTertiary },
                  ]}
                >
                  {t('portfolio.createCorporateAction', {
                    defaultValue: 'Create Corporate Action',
                  })}
                </Text>
              )}
            </TouchableOpacity>

            {onCancel && (
              <TouchableOpacity
                style={[
                  styles.button,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={onCancel}
                disabled={loading}
                activeOpacity={0.7}
              >
                <Text style={[styles.buttonText, { color: theme.colors.textSecondary }]}>
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Help Callout - rendered outside ScrollView, positioned next to icon */}
      {showDateHelp && helpIconLayout && (
        <View style={styles.helpCalloutContainer}>
          <TouchableOpacity
            style={styles.helpBackdrop}
            onPress={() => setShowDateHelp(false)}
            activeOpacity={1}
          />
          <View
            style={[
              styles.helpCallout,
              {
                top: helpIconLayout.y + helpIconLayout.height + 4,
                left: Math.max(16, helpIconLayout.x - 100),
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
              },
            ]}
          >
            <View style={[styles.helpCalloutArrow, { borderBottomColor: theme.colors.border }]} />
            <Text style={[styles.helpCalloutText, { color: theme.colors.text }]}>
              {t('portfolio.excludeDateHelp', {
                defaultValue:
                  'Trades ON or AFTER this date will NOT be affected by the corporate action. Only trades BEFORE this date will be updated.',
              })}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    outerContainer: {
      flex: 1,
      position: 'relative',
    },
    container: {
      flex: 1,
    },
    formContainer: {
      borderRadius: theme.radii.lg,
      padding: 16,
      margin: 16,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    fieldContainer: {
      marginBottom: 16,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
    labelWithHelp: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      gap: 6,
      marginBottom: 8,
    },
    labelInline: {
      marginBottom: 0,
    },
    helpIconWrapper: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    helpIconTouchable: {
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: 24,
      minHeight: 24,
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
    },
    textArea: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 80,
    },
    inputError: {
      borderColor: theme.colors.error,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
      color: theme.colors.error,
    },
    characterCount: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
      textAlign: 'right',
      color: theme.colors.textTertiary,
    },
    // Ratio input styles
    ratioInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
    },
    ratioInput: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
      minWidth: 70,
      textAlign: 'center',
    },
    flexInput: {
      flex: 1,
      minWidth: 60,
    },
    ratioLabel: {
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textSecondary,
    },
    hintBox: {
      marginTop: 8,
      padding: 10,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    hintText: {
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic',
    },
    // Action type buttons
    actionTypesContainer: {
      flexDirection: 'column',
      gap: 8,
    },
    actionTypeButton: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: theme.radii.md,
      alignItems: 'flex-start',
    },
    actionTypeTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 4,
    },
    actionTypeDescription: {
      fontSize: theme.typography.fontSizeXs,
    },
    // Buttons
    buttonContainer: {
      marginTop: 8,
    },
    button: {
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      minHeight: 48,
      justifyContent: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      marginBottom: 12,
    },
    primaryButton: {
      flex: 1,
    },
    buttonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    // Help callout styles (rendered outside ScrollView)
    helpCalloutContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'box-none',
      zIndex: 1000,
    },
    helpBackdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
    },
    helpCallout: {
      position: 'absolute',
      padding: 12,
      borderRadius: 8,
      borderWidth: 1,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 10,
      minWidth: 200,
      maxWidth: 320,
    },
    helpCalloutArrow: {
      position: 'absolute',
      top: -6,
      left: 20,
      width: 0,
      height: 0,
      borderLeftWidth: 6,
      borderRightWidth: 6,
      borderBottomWidth: 6,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
    },
    helpCalloutText: {
      fontSize: 12,
      lineHeight: 16,
    },
  });
}
